import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { Badge } from '@/components/shadcn/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { listMemoryLibraryIds } from '@/lib/api/memory'
import httpFetch from '@/lib/http'
import { Download } from 'lucide-react'

export default function ExportMemoriesPage() {
  const { data: libraryIds, isLoading } = useQuery({
    queryKey: ['memory-library-ids'],
    queryFn: () => listMemoryLibraryIds(),
  })

  const [memoryId, setMemoryId] = useState('')
  const selectedId = useMemo(() => memoryId || (libraryIds?.[0] ?? ''), [memoryId, libraryIds])

  useEffect(() => {
    if (!memoryId && libraryIds && libraryIds.length > 0) {
      setMemoryId(libraryIds[0])
    }
  }, [libraryIds])

  const handleDownload = async () => {
    if (!selectedId) return
    const url = `/api/v1/memory-lib/query/export-conversations?memory_id=${encodeURIComponent(selectedId)}`
    // 使用 httpFetch 保证带 Token 与 baseURL
    const res = await httpFetch(url, { method: 'POST' })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(`导出失败: ${res.status} ${txt}`)
    }
    const blob = await res.blob()
    const a = document.createElement('a')
    const objectUrl = URL.createObjectURL(blob)
    a.href = objectUrl
    a.download = `${selectedId}-conversations.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(objectUrl)
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">记忆库导出</h1>
          <p className="text-muted-foreground">导出指定记忆库的会话数据</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>选择记忆库</CardTitle>
          <CardDescription>选择目标记忆库并导出会话数据文件</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="w-72">
            <Select value={selectedId} onValueChange={setMemoryId} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={isLoading ? '加载中...' : '选择记忆库'} />
              </SelectTrigger>
              <SelectContent>
                {(libraryIds || []).map((id) => (
                  <SelectItem key={id} value={id}>{id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleDownload} disabled={!selectedId}>
              <Download className="mr-2 h-4 w-4" />
              下载导出文件
            </Button>
            {selectedId && (
              <Badge variant="secondary" className="text-xs">{selectedId}</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


